import React, { ReactNode } from 'react'
import { getClassNames } from '../../../_utils/classnames'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { getComputedCancelText, getComputedSubmitText, getSubmitProps, getVariantTtlConfig } from '../helpers'
import NotificationContentBuilder from './notification-content-builder'
import NotificationDeliveryBuilder from './notification-delivery-builder'
import { DeliveryChannelSelector, MacroType, Well } from '@pushly/aqe/lib/components'
import { DomainDto } from '../../../dtos/domain'
import { PromiseableProp } from '../../../_utils/promiseable-prop'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { NotificationContentModel } from '../../../models/notification/notification-content.model'
import { useService } from '../../../hooks/use-service'
import { AppState } from '../../../stores/app'
import { NotificationBuilderLevel, NotificationBuilderTheme } from '../enums'
import { AccountDto } from '../../../dtos/account.dto'
import { ActionDispatcher } from '../types'
import { NotificationVariantManager } from './notification-variant-manager'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { DeliveryChannel, NotificationAbTestType } from '@pushly/models/lib/enums'
import { useNotificationContentLoadingView } from '../hooks/use-notification-content-loading-view'
import { WarningFilled } from '@ant-design/icons'
import { Popover } from 'antd'

interface INotificationVariantBuilder {
    className?: string
    domain: DomainDto
    pageAnalysisDomain?: DomainDto
    account?: AccountDto
    builder: INotificationBuilderState
    id: number
    variant: NotificationVariantModel
    selected?: boolean
    dispatchChanges: ActionDispatcher
    onChange: (variant: NotificationVariantModel, id: number) => void
    keywordOptions?: PromiseableProp<any>
    customMacroOptions?: PromiseableProp<any>
    secretsMacroOptions?: PromiseableProp<any>
    macroCategories?: { urlFields?: MacroType[]; messageFields?: MacroType[] }
    onInitialVariantInit?: (variant: NotificationVariantModel) => void
    hideImageMacroToggle?: boolean
    imageMacroToggleLabel?: string
    imageMacroValue?: string
    imageMacroIcon?: ReactNode
    hideDelivery?: boolean
    hideTtlSettings?: boolean
    hidePageAnalysisToggle?: boolean
    hidePushlyAiToggle?: boolean
    isDraft?: boolean
    isTemplate?: boolean
    onChannelChange?: (change: DeliveryChannel[]) => void
    hideChannelSelector?: boolean
    hideVariantManager?: boolean
    variantManagerIsReadonly?: boolean
    variantManagerVariantActionsHidden?: boolean
    variantManagerPrimaryActionsHidden?: boolean
    variantManagerPrimaryActionsDisabled?: boolean
    variantManagerPrimaryActionsDisabledReason?: ReactNode
    //
    hideSubmit?: boolean
    submitText?: string
    cancelText?: string
    onSubmit?: (isDraft?: boolean) => void | Promise<void>
    onCancel?: () => void | Promise<void>
}

const NotificationVariantBuilder = (props: INotificationVariantBuilder) => {
    const {
        className,
        account,
        domain,
        pageAnalysisDomain,
        builder,
        id,
        variant,
        onChange,
        onSubmit,
        onCancel,
        dispatchChanges,
        onInitialVariantInit,
        onChannelChange,
        keywordOptions,
        customMacroOptions,
        secretsMacroOptions,
        hideImageMacroToggle,
        macroCategories,
        imageMacroToggleLabel,
        imageMacroValue,
        imageMacroIcon,
        hideDelivery,
        hideTtlSettings,
        isDraft,
        isTemplate,
        hideSubmit = false,
        submitText: submitTextOverride,
        cancelText: cancelTextOverride,
        hideChannelSelector = false,
        hideVariantManager = false,
        hidePageAnalysisToggle = false,
        hidePushlyAiToggle = false,
        variantManagerIsReadonly = false,
    } = props

    const { currentUser, currentDomain } = useService<AppState>(AppState)
    const level = builder.level
    const isOrgLevel = level === NotificationBuilderLevel.ORG
    const isDomainLevel = level === NotificationBuilderLevel.DOMAIN
    const isJourneyLevel = level === NotificationBuilderLevel.CAMPAIGN
    const levelClassName = isJourneyLevel ? 'journey' : isOrgLevel ? 'org' : 'domain'

    const meta = variant.getDisplayMeta()

    /**
     * ***
     * all internal user roles are prohibited from sending on non-demo|internal domains
     * exception: demo domains (isDemo) and demo template domains (isDemoTemplate) allow full write access
     * ***
     */
    const submitAllowed =
        !currentDomain?.isPreviewDomain &&
        (!currentUser?.isInternalUser ||
            currentDomain?.isDemoDomain ||
            currentDomain?.isDemo ||
            currentDomain?.isDemoTemplate ||
            currentDomain?.isInternal)

    const submitText = getComputedSubmitText({
        formMode: builder.mode,
        formType: isTemplate ? 'named-template' : 'send',
        variants: builder.variants,
        override: submitTextOverride,
    })

    const cancelText = getComputedCancelText(cancelTextOverride)

    const isMobileTheme = builder.theme === NotificationBuilderTheme.MOBILE
    const isNewNotification = !Object.keys(meta).length
    const isScheduledNotification = !isNewNotification && !meta.isDraft && !meta.notEditable

    let submitActions: Well['props']['submitActions']
    if (!isMobileTheme && !isTemplate && (isNewNotification || !isScheduledNotification) && isDomainLevel) {
        submitActions = [
            {
                text: meta.isDraft ? 'Update Draft ' : 'Save as Draft',
                onClick: () => onSubmit?.(true),
            },
        ]
    }

    const submitProps = getSubmitProps({
        hide: hideSubmit,
        disabled: !submitAllowed,
        onSubmit,
        onCancel,
        submitText,
        cancelText,
        submitActions,
    })

    const activeDomainChannels = getEnabledDeliveryChannels(domain, true)
    const canShowChannelSelector = !hideChannelSelector && !isJourneyLevel && activeDomainChannels.length > 1
    const canShowVariantManager = !hideVariantManager && !isJourneyLevel

    const contentWellActions = hideDelivery ? submitProps : { hideFooter: true }
    const deliveryWellActions = !hideDelivery ? submitProps : { hideFooter: true }

    const contentLoadingView = useNotificationContentLoadingView(builder)

    // Determines if legacy multi-variant delivery editing is allowed.
    // Multi-variant delivery editing is only permitted when:
    // 1. Editing an existing A/B test (builder.mode === 'edit')
    // 2. The test includes DELIVERY type testing (legacy behavior)
    // Otherwise, all variants share a single delivery configuration (default behavior).
    const allowLegacyDeliveryEdits =
        builder.mode === 'edit' && (builder.test?.getTestTypes().includes(NotificationAbTestType.DELIVERY) || false)

    const combineContentAndDeliveryWells = false

    return (
        <div
            className={getClassNames(
                'variant-wrapper',
                levelClassName,
                {
                    'selected-variant': id === builder.selectedVariantIdx,
                    conjoined: combineContentAndDeliveryWells,
                },
                className,
            )}
        >
            <Well
                className={getClassNames(
                    'variant-content-wrapper',
                    'nested',
                    isJourneyLevel && 'variant-content-wrapper__campaign-mode',
                    canShowChannelSelector && 'variant-content-wrapper__with-channels',
                    canShowVariantManager && 'variant-content-wrapper__with-variant-manager',
                )}
                title={hideDelivery || !combineContentAndDeliveryWells ? 'Content' : 'Content & Delivery'}
                {...contentWellActions}
            >
                {canShowChannelSelector && (
                    <DeliveryChannelSelector
                        className="nested mode-ghost"
                        type="multiple"
                        loading={builder.loading}
                        value={builder.channels}
                        onChange={onChannelChange!}
                        visibleChannels={activeDomainChannels}
                    />
                )}

                {canShowVariantManager && (
                    <NotificationVariantManager
                        state={builder}
                        dispatch={dispatchChanges}
                        readonly={variantManagerIsReadonly}
                        onInitialVariantInit={onInitialVariantInit}
                        account={account}
                        domain={domain}
                        campaign={builder.journey}
                    />
                )}

                <Well
                    className="notification-variant-content-wrapper"
                    mode="ghost"
                    hideHeader={true}
                    hideFooter={true}
                    bodyStyle={{ padding: 0 }}
                    loading={contentLoadingView}
                >
                    <NotificationContentBuilder
                        level={level}
                        theme={builder.theme}
                        loading={builder.loading}
                        builder={builder}
                        domain={domain}
                        pageAnalysisDomain={pageAnalysisDomain}
                        disabled={meta.notEditable}
                        experienceDisabled={meta.notEditable || meta.delivering}
                        value={variant.getContent()}
                        channels={builder.channels}
                        dispatchChanges={props.dispatchChanges}
                        onChange={(v) => {
                            const update = variant.clone()
                            update.setContent(v)
                            onChange(update, id)
                        }}
                        onClear={() => {
                            const update = variant.clone(false)
                            update.setContent(NotificationContentModel.build({}))

                            // reset default TTL
                            const expOpt = update.getContent().getDefaultContent().getExperienceOptions()
                            const ttlSet = getVariantTtlConfig(domain, expOpt)
                            expOpt.setTtlSeconds(ttlSet.seconds)
                            expOpt.setTtlMetric(ttlSet.metric)

                            onChange(update, id)
                        }}
                        keywordOptions={keywordOptions}
                        customMacroOptions={customMacroOptions}
                        secretsMacroOptions={secretsMacroOptions}
                        hideMacroToggle={hideImageMacroToggle}
                        macroCategories={macroCategories}
                        imageMacroToggleLabel={imageMacroToggleLabel}
                        imageMacroValue={imageMacroValue}
                        imageMacroIcon={imageMacroIcon}
                        hideTtlSettings={hideTtlSettings}
                        hidePageAnalysisToggle={hidePageAnalysisToggle}
                        hidePushlyAiToggle={hidePushlyAiToggle}
                    />
                </Well>
            </Well>

            {!hideDelivery && (
                <Well
                    className={getClassNames('variant-delivery-wrapper', 'nested')}
                    title={
                        !allowLegacyDeliveryEdits ? (
                            'Delivery'
                        ) : (
                            <span>
                                Delivery&nbsp;
                                <Popover
                                    placement="right"
                                    overlayStyle={{ maxWidth: 360 }}
                                    content={
                                        <div>
                                            You are currently editing an A/B Test with legacy settings. Changes to
                                            delivery will only affect the currently selected variant.
                                        </div>
                                    }
                                >
                                    <WarningFilled className="fg--color--orange-90" />
                                </Popover>
                            </span>
                        )
                    }
                    {...deliveryWellActions}
                >
                    <NotificationDeliveryBuilder
                        domain={domain}
                        account={account}
                        loading={builder.loading}
                        builder={builder}
                        hideTypeLabel={true}
                        defaultTimeZone={account?.timezone ?? domain.timezone}
                        disabled={meta.notEditable || meta.delivering}
                        value={variant.getDelivery()}
                        onChange={(v) => {
                            if (allowLegacyDeliveryEdits) {
                                // Individual delivery edits are only allowed when a legacy delivery test
                                // is detected and the builder is in edit mode.
                                dispatchChanges({
                                    type: 'patch',
                                    entity: 'delivery',
                                    data: {
                                        delivery: v,
                                        variantIdx: id,
                                    },
                                })
                            } else {
                                dispatchChanges({
                                    type: 'patch',
                                    entity: 'delivery',
                                    data: {
                                        delivery: v,
                                        variantIdx: 'all',
                                    },
                                })
                            }
                        }}
                    />
                </Well>
            )}
        </div>
    )
}

export default NotificationVariantBuilder
